/**
 * Shared helpers for the `schedule` and `alert` CLI command groups.
 *
 * Both groups are thin wrappers over the org-scoped automation API
 * (`/org/automation/*`). A scheduled test-suite run and a notify-only alert are
 * both rows in `automation_rules`; the two groups differ only in which flags
 * they expose and how they filter their `list`. This module holds everything
 * they have in common: argv parsing for timing/filter flags, the shared
 * list/get/pause/resume/delete/run subcommands, and row formatting.
 *
 * @module automation-shared
 */

import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest, fetchList } from './cli-helpers.js';
import { formatSlackChannel, loadSlackChannels, looksLikeChannelId } from './slack-channels.js';

export const RULES_PATH = '/org/automation/rules';
export const EXECUTIONS_PATH = '/org/automation/executions';
export const PARSE_SCHEDULE_PATH = '/org/automation/parse-schedule';

export type TriggerSource = 'github' | 'schedule' | 'system';

export interface ScheduleConfig {
  time?: string;
  timezone?: string;
  pattern?: string;
  intervalMs?: number;
}

export interface TriggerConfig {
  actions?: string[];
  repository_patterns?: string[];
  scheduleType?: 'simple' | 'cron' | 'interval';
  scheduleConfig?: ScheduleConfig;
}

export interface FilterCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'starts_with' | 'ends_with' | 'matches' | 'in';
  value: string | string[];
}

export interface AlertStep {
  channel: 'email' | 'slack';
  on: ('success' | 'failure')[];
  recipients?: string[];
  slackChannelId?: string;
}

export interface AutomationRule {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  triggerSource: TriggerSource;
  triggerEvent: string;
  triggerConfig: TriggerConfig;
  filterConfig: { conditions?: FilterCondition[] };
  delaySeconds: number | null;
  debounceEnabled: boolean;
  debounceKeyTemplate: string | null;
  actionType: string;
  actionConfig: Record<string, unknown>;
  alertConfig: { steps: AlertStep[] };
  scheduledJobId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface RuleResponse {
  ok: boolean;
  error?: string;
  data?: AutomationRule;
}

export interface AutomationExecution {
  id: string;
  ruleId: string;
  status: string;
  scheduledFor: string | null;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
  createdAt: string;
  rule?: { id: string; name: string } | null;
}

const FILTER_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'starts_with',
  'ends_with',
  'matches',
  'in',
] as const;

/* ── argv helpers ────────────────────────────────────────────────────── */

/** Collect every value for a repeatable flag (e.g. multiple `--tag`). */
export function getArgValues(argv: string[], key: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < argv.length - 1; i++) {
    if (argv[i] === key) out.push(argv[i + 1]);
  }
  return out;
}

/**
 * Collect a repeatable-or-comma-separated flag into a trimmed, de-duped list.
 * `--tag a --tag b,c` → `['a','b','c']`.
 */
export function collectList(argv: string[], key: string): string[] {
  const seen = new Set<string>();
  for (const raw of getArgValues(argv, key)) {
    for (const piece of raw.split(',')) {
      const v = piece.trim();
      if (v) seen.add(v);
    }
  }
  return [...seen];
}

export function fail(message: string): never {
  console.error(`Error: ${message}`);
  process.exit(1);
}

/* ── Timing flags → triggerConfig ────────────────────────────────────── */

/**
 * Build the schedule portion of a rule's `triggerConfig` from timing flags.
 * Exactly one of `--schedule`/`--cron`/`--at`/`--every` may be supplied.
 * Returns `undefined` when no timing flag is present (caller decides whether
 * that's an error — required on create, optional on edit).
 */
export async function buildScheduleTriggerConfig(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<TriggerConfig | undefined> {
  const nl = getArgValue(argv, '--schedule');
  const cron = getArgValue(argv, '--cron');
  const at = getArgValue(argv, '--at');
  const every = getArgValue(argv, '--every');
  const timezone = getArgValue(argv, '--timezone') ?? 'UTC';

  const provided = [nl, cron, at, every].filter((v) => v !== undefined);
  if (provided.length === 0) return undefined;
  if (provided.length > 1) {
    fail('Use only one of --schedule, --cron, --at, --every.');
  }

  if (nl !== undefined) {
    const parsed = await apiRequest<{ ok: boolean; error?: string; cron?: string }>(
      apiUrl,
      token,
      'POST',
      PARSE_SCHEDULE_PATH,
      { text: nl }
    );
    if (!parsed.ok || !parsed.cron) {
      fail(`Could not parse schedule "${nl}": ${parsed.error ?? 'invalid schedule'}`);
    }
    return { scheduleType: 'cron', scheduleConfig: { pattern: parsed.cron, timezone } };
  }

  if (cron !== undefined) {
    return { scheduleType: 'cron', scheduleConfig: { pattern: cron, timezone } };
  }

  if (at !== undefined) {
    if (!/^\d{1,2}:\d{2}$/.test(at)) {
      fail('--at must be HH:mm (24-hour), e.g. 02:00.');
    }
    return { scheduleType: 'simple', scheduleConfig: { time: at, timezone } };
  }

  // --every <n>(m|h)
  const match = /^(\d+)\s*(m|min|minutes?|h|hours?)$/i.exec(every!.trim());
  if (!match) {
    fail('--every must look like "30m" or "2h".');
  }
  const amount = Number.parseInt(match![1], 10);
  const unit = match![2].toLowerCase();
  const ms = unit.startsWith('h') ? amount * 3_600_000 : amount * 60_000;
  if (ms <= 0) fail('--every must be a positive duration.');
  return { scheduleType: 'interval', scheduleConfig: { intervalMs: ms, timezone } };
}

/** True when any timing flag is present (for edit-time conflict checks). */
export function hasTimingFlags(argv: string[]): boolean {
  return (
    getArgValue(argv, '--schedule') !== undefined ||
    getArgValue(argv, '--cron') !== undefined ||
    getArgValue(argv, '--at') !== undefined ||
    getArgValue(argv, '--every') !== undefined
  );
}

/* ── Filter flags → filterConfig.conditions ──────────────────────────── */

/**
 * Parse repeatable `--filter "<field>:<operator>:<value>"` flags. For the `in`
 * operator the value is split on commas into an array.
 */
export function parseFilters(argv: string[]): FilterCondition[] {
  const conditions: FilterCondition[] = [];
  for (const raw of getArgValues(argv, '--filter')) {
    const firstColon = raw.indexOf(':');
    const secondColon = raw.indexOf(':', firstColon + 1);
    if (firstColon === -1 || secondColon === -1) {
      fail(`--filter must be "field:operator:value" (got "${raw}").`);
    }
    const field = raw.slice(0, firstColon).trim();
    const operator = raw.slice(firstColon + 1, secondColon).trim();
    const rawValue = raw.slice(secondColon + 1);
    if (!field) fail(`--filter is missing a field (got "${raw}").`);
    if (!(FILTER_OPERATORS as readonly string[]).includes(operator)) {
      fail(`--filter operator must be one of: ${FILTER_OPERATORS.join(', ')} (got "${operator}").`);
    }
    const value =
      operator === 'in'
        ? rawValue
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : rawValue;
    conditions.push({ field, operator: operator as FilterCondition['operator'], value });
  }
  return conditions;
}

/* ── Alert steps (--email / --slack / --when) ────────────────────────── */

/** Parse `--when` into a non-empty `on` list (defaults to failure-only). */
export function parseWhen(argv: string[]): ('success' | 'failure')[] {
  const raw = getArgValue(argv, '--when');
  if (!raw) return ['failure'];
  const values = raw
    .split(',')
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);
  for (const v of values) {
    if (v !== 'success' && v !== 'failure') fail('--when must be "success", "failure", or both.');
  }
  if (values.length === 0) return ['failure'];
  return values as ('success' | 'failure')[];
}

/**
 * Build `alertConfig.steps` from `--email`/`--slack`/`--when` flags, or `null`
 * when no channel flag is supplied. `--slack` accepts a channel ID (`C…`), a
 * `#name`, or a bare name; non-ID values are resolved against the org's Slack
 * channels and fail loudly when unmatched.
 */
export async function buildSteps(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<AlertStep[] | null> {
  const emails = collectList(argv, '--email');
  const slackValues = collectList(argv, '--slack');
  if (emails.length === 0 && slackValues.length === 0) return null;

  const on = parseWhen(argv);
  const steps: AlertStep[] = [];
  if (emails.length > 0) steps.push({ channel: 'email', on, recipients: emails });

  // Only hit the Slack API when a value needs name→id resolution.
  const needsResolution = slackValues.some((v) => !looksLikeChannelId(v));
  const nameToId = needsResolution ? (await loadSlackChannels(apiUrl, token)).nameToId : null;

  for (const value of slackValues) {
    let channelId = value;
    if (!looksLikeChannelId(value)) {
      const name = value.replace(/^#/, '').toLowerCase();
      const resolved = nameToId?.get(name);
      if (!resolved) {
        fail(
          `Slack channel "${value}" not found. Pass a channel ID (C…), or check the name ` +
            `and that the Cofactor Slack bot is a member of that channel.`
        );
      }
      channelId = resolved;
    }
    steps.push({ channel: 'slack', on, slackChannelId: channelId });
  }
  return steps;
}

/** Stable identity for an alert step, used to de-dupe when merging. */
function stepKey(s: AlertStep): string {
  return s.channel === 'slack'
    ? `slack:${s.slackChannelId ?? ''}`
    : `email:${[...(s.recipients ?? [])].sort().join(',')}`;
}

/**
 * Merge `incoming` alert steps into `existing`, keyed by channel target.
 * Incoming steps override an existing step with the same target (so re-adding a
 * channel updates its `on`), and new targets are appended.
 */
export function mergeSteps(existing: AlertStep[], incoming: AlertStep[]): AlertStep[] {
  const byKey = new Map<string, AlertStep>();
  for (const s of existing) byKey.set(stepKey(s), s);
  for (const s of incoming) byKey.set(stepKey(s), s);
  return [...byKey.values()];
}

/* ── Shared subcommands ──────────────────────────────────────────────── */

export async function getRuleOrExit(
  apiUrl: string,
  token: string,
  id: string
): Promise<AutomationRule> {
  const res = await apiRequest<RuleResponse>(apiUrl, token, 'GET', `${RULES_PATH}/${id}`);
  if (!res.ok || !res.data) fail(res.error ?? `Rule ${id} not found`);
  return res.data!;
}

export async function handleGet(
  argv: string[],
  apiUrl: string,
  token: string,
  noun: string
): Promise<void> {
  const id = argv[0];
  if (!id) fail(`Usage: ${noun} get <id>`);
  const rule = await getRuleOrExit(apiUrl, token, id);
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(rule, null, 2));
    return;
  }
  const idToName = await channelMapIfNeeded([rule], apiUrl, token);
  printRuleDetail(rule, idToName);
}

export async function handleList(
  apiUrl: string,
  token: string,
  query: Record<string, string | undefined>,
  argv: string[],
  noun: string
): Promise<void> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, v);
  }
  const enabled = getArgValue(argv, '--enabled');
  if (enabled === 'true' || enabled === 'false') params.set('enabled', enabled);
  const page = getArgValue(argv, '--page');
  if (page) params.set('page', page);
  params.set('pageSize', getArgValue(argv, '--page-size') ?? '50');

  const rules = await fetchList<AutomationRule>(
    apiUrl,
    token,
    `${RULES_PATH}?${params.toString()}`,
    'data'
  );

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(rules, null, 2));
    return;
  }
  if (rules.length === 0) {
    console.log(`No ${noun} rules found.`);
    return;
  }
  const idToName = await channelMapIfNeeded(rules, apiUrl, token);
  for (const rule of rules) {
    const state = rule.enabled ? 'enabled' : 'paused';
    console.log(`  ${rule.id}  ${rule.name}  [${state}]  ${describeRule(rule, idToName)}`);
  }
}

export async function handlePauseResume(
  argv: string[],
  apiUrl: string,
  token: string,
  desiredEnabled: boolean,
  noun: string
): Promise<void> {
  const id = argv[0];
  if (!id) fail(`Usage: ${noun} ${desiredEnabled ? 'resume' : 'pause'} <id>`);
  const rule = await getRuleOrExit(apiUrl, token, id);
  if (rule.enabled === desiredEnabled) {
    console.log(`${rule.name} is already ${desiredEnabled ? 'enabled' : 'paused'}.`);
    return;
  }
  const res = await apiRequest<RuleResponse>(apiUrl, token, 'POST', `${RULES_PATH}/${id}/toggle`);
  if (!res.ok) fail(res.error ?? 'Toggle failed');
  console.log(`${desiredEnabled ? 'Resumed' : 'Paused'}: ${rule.name}`);
}

export async function handleDelete(
  argv: string[],
  apiUrl: string,
  token: string,
  noun: string
): Promise<void> {
  const id = argv[0];
  if (!id) fail(`Usage: ${noun} delete <id>`);
  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `${RULES_PATH}/${id}`
  );
  if (!res.ok) fail(res.error ?? 'Delete failed');
  console.log(`Deleted ${id}`);
}

export async function handleRun(
  argv: string[],
  apiUrl: string,
  token: string,
  noun: string
): Promise<void> {
  const id = argv[0];
  if (!id) fail(`Usage: ${noun} run <id>`);
  const res = await apiRequest<{ ok: boolean; error?: string; executionId?: string }>(
    apiUrl,
    token,
    'POST',
    `${RULES_PATH}/${id}/run`
  );
  if (!res.ok) fail(res.error ?? 'Run failed');
  console.log(`Started execution ${res.executionId ?? '(queued)'}`);
}

export async function handleRuns(
  argv: string[],
  apiUrl: string,
  token: string,
  noun: string
): Promise<void> {
  const id = getArgValue(argv, '--rule') ?? argv[0];
  if (!id) fail(`Usage: ${noun} runs <id> [--status <s>]`);
  const params = new URLSearchParams({ ruleId: id, pageSize: getArgValue(argv, '--page-size') ?? '20' });
  const status = getArgValue(argv, '--status');
  if (status) params.set('status', status);
  const page = getArgValue(argv, '--page');
  if (page) params.set('page', page);

  const executions = await fetchList<AutomationExecution>(
    apiUrl,
    token,
    `${EXECUTIONS_PATH}?${params.toString()}`,
    'data'
  );
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(executions, null, 2));
    return;
  }
  if (executions.length === 0) {
    console.log('No executions found.');
    return;
  }
  for (const e of executions) {
    const when = e.startedAt ?? e.scheduledFor ?? e.createdAt;
    const err = e.error ? `  ${e.error}` : '';
    console.log(`  ${e.id}  ${e.status.padEnd(10)}  ${when}${err}`);
  }
}

/* ── Create/edit submission ──────────────────────────────────────────── */

/** POST a new rule and print the result. */
export async function createRule(
  apiUrl: string,
  token: string,
  body: Record<string, unknown>
): Promise<void> {
  const res = await apiRequest<RuleResponse>(apiUrl, token, 'POST', RULES_PATH, body);
  if (!res.ok || !res.data) fail(res.error ?? 'Create failed');
  console.log(`Created ${res.data!.id}  ${res.data!.name}`);
}

/** PUT changes to an existing rule and print the result. */
export async function updateRule(
  apiUrl: string,
  token: string,
  id: string,
  body: Record<string, unknown>
): Promise<void> {
  if (Object.keys(body).length === 0) fail('No changes supplied.');
  const res = await apiRequest<RuleResponse>(apiUrl, token, 'PUT', `${RULES_PATH}/${id}`, body);
  if (!res.ok || !res.data) fail(res.error ?? 'Update failed');
  console.log(`Updated ${res.data!.id}  ${res.data!.name}`);
}

/* ── Formatting ──────────────────────────────────────────────────────── */

function describeSchedule(tc: TriggerConfig): string {
  const sc = tc.scheduleConfig ?? {};
  if (tc.scheduleType === 'cron') return `cron ${sc.pattern ?? '?'}`;
  if (tc.scheduleType === 'simple') return `daily ${sc.time ?? '?'} ${sc.timezone ?? ''}`.trim();
  if (tc.scheduleType === 'interval') return `every ${(sc.intervalMs ?? 0) / 60000}m`;
  return 'scheduled';
}

function describeTarget(rule: AutomationRule): string {
  const cfg = rule.actionConfig ?? {};
  if (typeof cfg.suite_id === 'string') return `suite ${cfg.suite_id}`;
  if (Array.isArray(cfg.tags) && cfg.tags.length > 0) return `tags ${(cfg.tags as string[]).join(',')}`;
  return 'all published';
}

function describeChannels(rule: AutomationRule, idToName?: Map<string, string>): string {
  const steps = rule.alertConfig?.steps ?? [];
  if (steps.length === 0) return 'no channels';
  return steps
    .map((s) =>
      s.channel === 'email'
        ? `email→${(s.recipients ?? []).join(',')}`
        : `slack→${s.slackChannelId ? formatSlackChannel(s.slackChannelId, idToName) : '?'}`
    )
    .join(' ');
}

/** Whether a rule carries any Slack alert step (drives the name-map fetch). */
function hasSlackStep(rule: AutomationRule): boolean {
  return (rule.alertConfig?.steps ?? []).some((s) => s.channel === 'slack' && !!s.slackChannelId);
}

/**
 * Load the channel id→name map for display, but only when at least one rule has
 * a Slack step. Best-effort: returns `undefined` so callers fall back to IDs.
 */
async function channelMapIfNeeded(
  rules: AutomationRule[],
  apiUrl: string,
  token: string
): Promise<Map<string, string> | undefined> {
  if (!rules.some(hasSlackStep)) return undefined;
  return (await loadSlackChannels(apiUrl, token)).idToName;
}

/** One-line summary used in `list` rows, dispatching on rule kind. */
export function describeRule(rule: AutomationRule, idToName?: Map<string, string>): string {
  if (rule.actionType === 'notify') {
    const trigger =
      rule.triggerSource === 'schedule'
        ? describeSchedule(rule.triggerConfig)
        : rule.triggerEvent;
    return `${trigger} → ${describeChannels(rule, idToName)}`;
  }
  const base = `${describeSchedule(rule.triggerConfig)} → ${describeTarget(rule)}`;
  // Schedules can carry their own alert steps; surface them inline when present.
  return (rule.alertConfig?.steps ?? []).length > 0
    ? `${base}  |  alerts: ${describeChannels(rule, idToName)}`
    : base;
}

function printRuleDetail(rule: AutomationRule, idToName?: Map<string, string>): void {
  console.log(`${rule.name}  (${rule.id})`);
  if (rule.description) console.log(`  ${rule.description}`);
  console.log(`  enabled:     ${rule.enabled}`);
  console.log(`  trigger:     ${rule.triggerSource} / ${rule.triggerEvent}`);
  if (rule.triggerSource === 'schedule') {
    console.log(`  schedule:    ${describeSchedule(rule.triggerConfig)}`);
  }
  if (rule.actionType === 'notify') {
    console.log(`  channels:    ${describeChannels(rule, idToName)}`);
  } else {
    console.log(`  action:      ${rule.actionType}`);
    console.log(`  target:      ${describeTarget(rule)}`);
    if ((rule.alertConfig?.steps ?? []).length > 0) {
      console.log(`  channels:    ${describeChannels(rule, idToName)}`);
    }
  }
  const conditions = rule.filterConfig?.conditions ?? [];
  if (conditions.length > 0) {
    console.log('  filters:');
    for (const c of conditions) {
      const v = Array.isArray(c.value) ? c.value.join(',') : c.value;
      console.log(`    - ${c.field} ${c.operator} ${v}`);
    }
  }
}
